import React, { useEffect, useMemo, useState } from 'react'

import { useMediaQueryContext } from '@fourthwall/components'

import { AddSectionModalView } from './AddSectionModalView'
import { useAvailableSections } from './hooks/useAvailableSections'
import { useInstalledPlatformApps } from './hooks/useInstalledPlatformApps'
import { useSection } from './hooks/useSection'
import { useSectionConfigurations } from './hooks/useSectionConfigurations'
import type { CustomSection, Section } from './types'

type Props = {
  blockId: string
  type: string
  position?: number
}

export const AddSectionModalContainer = ({ blockId, type, position }: Props) => {
  const [selectedSection, setSelectedSection] = useState<Section | CustomSection | null>(null)
  const media = useMediaQueryContext()

  const { sectionConfigurations } = useSectionConfigurations(type)
  const { sectionGroups } = useAvailableSections({
    sections: sectionConfigurations,
  })
  const { createLayoutSection } = useSection()
  const { installedPlatformApps } = useInstalledPlatformApps({
    enabled: true,
  })

  useEffect(() => {
    if (sectionGroups && media.tablet && !selectedSection) {
      if (blockId === 'content_for_index') {
        setSelectedSection('Recommended')
      } else {
        setSelectedSection(sectionGroups[0].sections[0])
      }
    }
  }, [sectionGroups, media, selectedSection])

  const handleCreateLayoutSection = (
    sectionType: string,
    blockId: string,
    appId?: string,
    presetName?: string,
  ) => {
    const defaultPosition = type === 'header' ? 1 : undefined

    createLayoutSection(sectionType, blockId, position || defaultPosition, appId, presetName)
  }

  const mergedSectionGroups = useMemo(() => {
    if (!sectionGroups) return undefined

    const appGroups =
      installedPlatformApps
        ?.filter((app) => app.sections.length > 0 && blockId === 'content_for_index')
        .map((app) => {
          return {
            group: app.displayName,
            sections: app.sections.map((section) => ({
              sectionType: section.id,
              name: section.name || '',
              maxBlocks: 1,
              platformAppId: app.id,
              additionalInfo: {
                description: section.description || '',
                groupName: app.displayName,
                position: 1,
              },
              presets: [],
            })),
          }
        }) || []

    return [...sectionGroups, ...appGroups]
  }, [sectionGroups, installedPlatformApps, blockId])

  return (
    <AddSectionModalView
      blockId={blockId}
      sections={mergedSectionGroups}
      selectedSection={selectedSection}
      onCreateLayoutSection={handleCreateLayoutSection}
      filterSections={[]}
      onSectionSelect={setSelectedSection}
    />
  )
}
